import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Navigatesales = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { editData, editId } = location.state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [salesData, setSalesData] = useState({
    Sales_ID: "",
    Purchase_Date: new Date().toISOString().split("T")[0],
    Customer_Name: "",
    mobile_number: "",
    GSTIN: "",
    Sub_Total: 0,
    Gst_Amount: 0,
    Net_total: 0,
    Paid_Amount: 0,
  });
  const [salesItems, setSalesItems] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    Date: new Date().toISOString().split("T")[0],
    Pay_Type: "",
    Amount: "",
  });
  const [paymentEditIndex, setPaymentEditIndex] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryTableData, setCategoryTableData] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [generatedProductId, setGeneratedProductId] = useState("");
  const [quantityInputs, setQuantityInputs] = useState({});
  const [isGstDisabled, setIsGstDisabled] = useState(false);
  const [originalGstValues, setOriginalGstValues] = useState({});

  const [productData, setProductData] = useState({
    hsn_code: "",
    category: "",
    subcategory: "",
    product_name: "",
    branch_name: "Default Branch",
    brand_name: "",
    type: "",
    tyre_size: "",
    status: "Active",
    purchase_rate: "",
    gst: "",
    available_quantity: "",
    mrp: "",
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          salesIdRes,
          stockRes,
          productIdRes,
          categoryRes,
        ] = await Promise.all([
          fetchNextSalesId().catch(() => null),
          fetchStockData().catch(() => null),
          fetchNextProductId().catch(() => null),
          addCategory().catch(() => null),
        ]);
        if (
          !salesIdRes ||
          !stockRes ||
          !productIdRes ||
          !categoryRes
        ) {
          // toast.error("Some data failed to load. Please try again.");
        }
      } catch (err) {
        toast.error("Failed to initialize data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle edit mode
  useEffect(() => {
    if (editId && editData) {
      setSalesData({
        Sales_ID: editData.Sales_ID,
        Purchase_Date: editData.Purchase_Date,
        Customer_Name: editData.name || "",
        mobile_number: editData.mobile_number || "",
        GSTIN: editData.GSTIN || "",
        Sub_Total: parseFloat(editData.Sub_Total) || 0,
        Gst_Amount: parseFloat(editData.Gst_Amount) || 0,
        Net_total: parseFloat(editData.Net_total) || 0,
        Paid_Amount: parseFloat(editData.Paid_Amount) || 0,
      });
      fetchSalesItems(editData.Sales_ID);
      fetchPaymentHistory(editData.Sales_ID);
    }
  }, [editId, editData]);

  // Fetch next sales ID
  const fetchNextSalesId = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/sales_data.php/generate-Sales-code`
      );
      if (response.data?.Sales_ID) {
        setSalesData((prev) => ({ ...prev, Sales_ID: response.data.Sales_ID }));
      } else {
        throw new Error("Failed to generate Sales ID.");
      }
    } catch (error) {
      toast.error("Failed to fetch Sales ID.");
      throw error;
    }
  };

  // Fetch stock data
  const fetchStockData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/stock_data.php`);
      let fetchedData = response.data.data || [];
      if (fetchedData.some((item) => !item.hsn_code)) {
        toast.warn("Some stock items are missing HSN codes.");
      }
      setStockData(fetchedData);
      const initialQuantities = {};
      const initialQuantityInputs = {};
      fetchedData.forEach((item) => {
        initialQuantities[item.hsn_code] = 0;
        initialQuantityInputs[item.hsn_code] = item.available_quantity;
      });
      setQuantities(initialQuantities);
      setQuantityInputs(initialQuantityInputs);
    } catch (error) {
      console.error("Error fetching stock data:", error);
      setError("No stock data available");
      throw error;
    }
  };

  // Fetch product categories
  const addCategory = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/product_category.php`);
      let categoriesMap = {};
      if (response.data && response.data.data) {
        response.data.data.forEach((item) => {
          if (!item.category) return;
          if (!categoriesMap[item.category]) {
            categoriesMap[item.category] = new Set();
          }
          if (item.subcategory && typeof item.subcategory === "string") {
            item.subcategory
              .split(",")
              .map((subCat) => subCat.trim())
              .filter((subCat) => subCat.length > 0)
              .forEach((subCat) => {
                categoriesMap[item.category].add(subCat);
              });
          }
        });
      }
      const transformedData = Object.keys(categoriesMap).map((category) => ({
        category,
        subcategories: Array.from(categoriesMap[category]),
      }));
      setCategoryTableData(transformedData);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoryTableData([]);
      toast.error("Failed to fetch categories.");
      throw error;
    }
  };

  // Fetch next product ID
  const fetchNextProductId = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/product_data.php/generate-product-code`
      );
      setGeneratedProductId(response.data.hsn_code);
      setProductData((prev) => ({
        ...prev,
        hsn_code: response.data?.hsn_code,
      }));
    } catch (error) {
      console.error("Error fetching product ID:", error);
      toast.error("Failed to fetch product ID.");
      throw error;
    }
  };

  // Fetch sales items
  const fetchSalesItems = async (salesId) => {
    try {
      const response = await axios.get(`${ApiUrl}/sales_data.php`, {
        params: { Sales_ID: salesId },
      });
      if (response.data?.data) {
        const mappedItems = response.data.data[0].Sales_Items.map((item) => {
          let tyreNums = item.tyre_number
            ? item.tyre_number.split(",").map((t) => t.trim())
            : [];
          const qty = parseInt(item.sales_quantity) || 0;
          if (tyreNums.length < qty) {
            tyreNums = [...tyreNums, ...Array(qty - tyreNums.length).fill("")];
          } else if (tyreNums.length > qty) {
            tyreNums = tyreNums.slice(0, qty);
          }
          return {
            Sales_ID: item.Sales_ID || salesId,
            branch_name: item.branch_name || "Default Branch",
            category: item.category || "N/A",
            sub_category: item.sub_category || "N/A",
            type: item.type || "N/A",
            tyre_size: item.tyre_size || "N/A",
            hsn_code: item.HSN_code || item.hsn_code || "",
            sales_quantity: qty,
            sales_rate: parseFloat(item.sales_rate) || 1000,
            sales_gst: parseFloat(item.sales_gst) || 18,
            tyre_numbers: tyreNums,
            Mrp: parseFloat(item.Mrp) || 0,
          };
        });
        setSalesItems(mappedItems);
        const gstValues = {};
        mappedItems.forEach((item) => {
          gstValues[item.hsn_code] = parseFloat(item.sales_gst) || 18;
        });
        setOriginalGstValues(gstValues);
        updateQuantitiesFromSalesItems(mappedItems);
      }
    } catch (error) {
      console.error("Error fetching sales items:", error);
      toast.error("Failed to fetch sales items.");
    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async (salesId) => {
    try {
      const response = await axios.get(`${ApiUrl}/sales_payment_history.php`, {
        params: { Sales_ID: salesId },
      });
      if (response.data?.data) {
        const payments = response.data.data.map((payment) => ({
          Date: payment.Date,
          Pay_Type: payment.Pay_Type,
          Amount: parseFloat(payment.Amount) || 0,
        }));
        setPaymentHistory(payments);
        const totalPaid = payments.reduce(
          (sum, payment) => sum + parseFloat(payment.Amount || 0),
          0
        );
        setSalesData((prev) => ({ ...prev, Paid_Amount: totalPaid }));
      } else {
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to fetch payment history.");
      setPaymentHistory([]);
    }
  };

  // Update quantities from sales items
  const updateQuantitiesFromSalesItems = (items) => {
    const newQuantities = { ...quantities };
    items.forEach((item) => {
      const stockItem = stockData.find(
        (stock) => stock.hsn_code === item.hsn_code
      );
      if (stockItem) {
        newQuantities[stockItem.hsn_code] = parseInt(item.sales_quantity) || 0;
      }
    });
    setQuantities(newQuantities);
  };

  // Handle sales data change
  const handleSalesDataChange = (e) => {
    const { name, value } = e.target;
    setSalesData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  // Handle quantity change for sales items
  const handleQuantityChange = (item, type) => {
    const currentStock = parseInt(item.available_quantity) || 0;
    const currentQuantity = quantities[item.hsn_code] || 0;
    let newQuantity =
      type === "increase"
        ? currentQuantity + 1
        : Math.max(0, currentQuantity - 1);

    if (type === "increase" && newQuantity > currentStock) {
      toast.error(
        `Cannot add more. Available quantity for ${item.category} - ${item.tyre_size} is ${currentStock}.`
      );
      return;
    }

    if (!item.hsn_code) {
      toast.error(
        `HSN code is missing for ${item.category} - ${item.tyre_size}.`
      );
      return;
    }

    setQuantities((prev) => ({ ...prev, [item.hsn_code]: newQuantity }));

    setSalesItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex(
        (salesItem) => salesItem.hsn_code === item.hsn_code
      );
      if (existingItemIndex !== -1) {
        const updatedItems = [...prevItems];
        const existingItem = updatedItems[existingItemIndex];
        let newTyreNumbers = [...existingItem.tyre_numbers];
        if (newQuantity > existingItem.sales_quantity) {
          const diff = newQuantity - existingItem.sales_quantity;
          newTyreNumbers = [...newTyreNumbers, ...Array(diff).fill("")];
        } else if (newQuantity < existingItem.sales_quantity) {
          newTyreNumbers = newTyreNumbers.slice(0, newQuantity);
        }
        if (type === "decrease" && newQuantity === 0) {
          updatedItems.splice(existingItemIndex, 1);
        } else {
          updatedItems[existingItemIndex] = {
            ...existingItem,
            sales_quantity: newQuantity,
            tyre_numbers: newTyreNumbers,
          };
        }
        return updatedItems;
      } else if (type === "increase" && newQuantity > 0) {
        const gstValue = isGstDisabled
          ? 0
          : originalGstValues[item.hsn_code] || parseFloat(item.gst) || 18;
        const gstRate = gstValue / 100;
        const mrp = parseFloat(item.mrp) || 0;
        const salesRate = (mrp / (1 + gstRate)).toFixed(2);
        const newItem = {
          Sales_ID: salesData.Sales_ID,
          branch_name: item.branch_name || "Default Branch",
          category: item.category || "N/A",
          sub_category: item.subcategory || "N/A",
          type: item.type || "N/A",
          tyre_size: item.tyre_size || "N/A",
          hsn_code: item.hsn_code,
          sales_quantity: newQuantity,
          sales_rate: parseFloat(salesRate),
          sales_gst: gstValue,
          tyre_numbers: Array(newQuantity).fill(""),
          Mrp: mrp,
        };
        setOriginalGstValues((prev) => ({
          ...prev,
          [item.hsn_code]: newItem.sales_gst,
        }));
        return [...prevItems, newItem];
      }
      return prevItems;
    });
  };

  // Handle stock quantity change
  const handleStockQuantityChange = async (item, value) => {
    const newQty = parseInt(value) || 0;
    if (newQty < 0) {
      toast.error("Quantity cannot be negative.");
      return;
    }

    setQuantityInputs((prev) => ({ ...prev, [item.hsn_code]: value }));

    try {
      await axios.put(`${ApiUrl}/stock_data.php`, {
        hsn_code: item.hsn_code,
        available_quantity: newQty,
      });
      setStockData((prev) =>
        prev.map((stock) =>
          stock.hsn_code === item.hsn_code
            ? { ...stock, available_quantity: newQty.toString() }
            : stock
        )
      );
      setQuantities((prev) => {
        const currentSalesQuantity = prev[item.hsn_code] || 0;
        if (currentSalesQuantity > newQty) {
          toast.warn(
            `Adjusted sales quantity for ${item.category} - ${item.tyre_size} to match new stock: ${newQty}`
          );
          return { ...prev, [item.hsn_code]: newQty };
        }
        return prev;
      });
      setSalesItems((prevItems) =>
        prevItems
          .map((salesItem) =>
            salesItem.hsn_code === item.hsn_code
              ? {
                ...salesItem,
                sales_quantity: Math.min(salesItem.sales_quantity, newQty),
              }
              : salesItem
          )
          .filter((salesItem) => salesItem.sales_quantity > 0)
      );
    } catch (error) {
      console.error("Error updating stock quantity:", error);
      toast.error(
        error.response?.data?.message || "Failed to update stock quantity."
      );
    }
  };

  // Handle sales item field change
  const handleSalesItemChange = (index, field, value) => {
    setSalesItems((prevItems) => {
      const updatedItems = [...prevItems];
      const item = updatedItems[index];
      if (field === "sales_quantity") {
        const newQty = parseInt(value) || 0;
        const stockItem = stockData.find(
          (stock) => stock.hsn_code === item.hsn_code
        );
        if (stockItem && newQty > parseInt(stockItem.available_quantity)) {
          toast.error(
            `Cannot set quantity to ${newQty}. Available: ${stockItem.available_quantity}`
          );
          return prevItems;
        }
        let newTyreNumbers = [...item.tyre_numbers];
        if (newQty > item.sales_quantity) {
          const diff = newQty - item.sales_quantity;
          newTyreNumbers = [...newTyreNumbers, ...Array(diff).fill("")];
        } else if (newQty < item.sales_quantity) {
          newTyreNumbers = newTyreNumbers.slice(0, newQty);
        }
        updatedItems[index] = {
          ...item,
          sales_quantity: newQty,
          tyre_numbers: newTyreNumbers,
        };
        setQuantities((prev) => ({ ...prev, [item.hsn_code]: newQty }));
      } else if (field === "Mrp") {
        const newMrp = parseFloat(value) || 0;
        const gstRate = parseFloat(item.sales_gst || 0) / 100;
        const newSalesRate = (newMrp / (1 + gstRate)).toFixed(2);
        updatedItems[index] = {
          ...item,
          Mrp: newMrp.toFixed(2),
          sales_rate: parseFloat(newSalesRate),
        };
      } else if (field === "sales_gst") {
        const gstValue = value === "" ? "" : parseFloat(value) || 0;
        if (gstValue < 0 || gstValue >= 100) {
          toast.error("GST must be between 0 and 99.99.");
          return prevItems;
        }
        const mrp = parseFloat(item.Mrp || 0);
        const gstRate = gstValue / 100;
        const newSalesRate = (mrp / (1 + gstRate)).toFixed(2);
        updatedItems[index] = {
          ...item,
          sales_gst: gstValue,
          sales_rate: parseFloat(newSalesRate),
        };
      }
      return updatedItems;
    });
  };

  // Handle tyre number change
  const handleTyreNumberChange = (index, tyreIdx, value) => {
    setSalesItems((prevItems) => {
      const updatedItems = [...prevItems];
      const item = { ...updatedItems[index] };
      const newTyreNumbers = [...item.tyre_numbers];
      newTyreNumbers[tyreIdx] = value;
      item.tyre_numbers = newTyreNumbers;
      updatedItems[index] = item;
      return updatedItems;
    });
  };

  // Delete sales item
  const deleteSalesItem = (index) => {
    const item = salesItems[index];
    setQuantities((prev) => ({ ...prev, [item.hsn_code]: 0 }));
    setSalesItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  // Edit sales item
  const editSalesItem = (item, index) => {
    const stockItem = stockData.find(
      (stock) => stock.hsn_code === item.hsn_code
    );
    if (stockItem) {
      const newQuantity = parseInt(item.sales_quantity) || 0;
      if (newQuantity > parseInt(stockItem.available_quantity)) {
        toast.error(
          `Cannot edit to ${newQuantity}. Available quantity for ${item.category} - ${item.tyre_size} is ${stockItem.available_quantity}.`
        );
        return;
      }
      setQuantities((prev) => ({ ...prev, [item.hsn_code]: newQuantity }));
      setSalesItems((prevItems) => {
        const updatedItems = [...prevItems];
        updatedItems[index] = { ...item, sales_quantity: newQuantity };
        return updatedItems;
      });
    }
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return salesItems.reduce((total, item) => {
      const gstRate = parseFloat(item.sales_gst || 0) / 100;
      const mrpIncludingGst = parseFloat(item.Mrp || 0);
      const baseMrp1 = (mrpIncludingGst * (1 + gstRate)).toFixed(2);
      const baseMrp2 = (baseMrp1 - mrpIncludingGst).toFixed(2);
      const baseMrp = (mrpIncludingGst - baseMrp2).toFixed(2);
      return total + parseFloat(item.sales_quantity || 0) * baseMrp;
    }, 0);
  };




  const calculateGstAmount = () => {
    if (isGstDisabled) return 0;
    return salesItems.reduce((total, item) => {
      const gstRate = parseFloat(item.sales_gst || 0) / 100;
      const mrpIncludingGst = parseFloat(item.Mrp || 0);
      const baseMrp1 = (mrpIncludingGst * (1 + gstRate)).toFixed(2);
      const basePrice = mrpIncludingGst / (1 + gstRate);
      const gstAmount = baseMrp1 - mrpIncludingGst;
      return total + parseFloat(item.sales_quantity || 0) * gstAmount;
    }, 0);
  };

  const calculateNetTotal = () => {
    return salesItems.reduce((total, item) => {
      const mrpIncludingGst = parseFloat(item.Mrp || 0);
      return total + parseFloat(item.sales_quantity || 0) * mrpIncludingGst;
    }, 0);
  };

  // Handle GST checkbox change
  const handleGstCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setIsGstDisabled(isChecked);
    setSalesItems((prevItems) =>
      prevItems.map((item) => {
        const newGst = isChecked
          ? 0
          : originalGstValues[item.hsn_code] ||
          stockData.find((stock) => stock.hsn_code === item.hsn_code)?.gst ||
          18;
        const gstRate = newGst / 100;
        const mrp = parseFloat(item.Mrp || 0);
        // const newSalesRate = (mrp / (1 + gstRate)).toFixed(2);
        const newSalesRate = (mrp).toFixed(2);
        return {
          ...item,
          sales_gst: newGst,
          sales_rate: parseFloat(newSalesRate),
        };
      })
    );
  };

  // Handle payment form change
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle payment submission
  const handlePaymentSubmit = () => {
    const { Date: paymentDate, Pay_Type, Amount } = paymentForm;

    if (!paymentDate || !Pay_Type || !Amount || parseFloat(Amount) <= 0) {
      toast.error("Please fill all payment fields with valid values.");
      return;
    }

    const newPayment = {
      Date: paymentDate,
      Pay_Type,
      Amount: parseFloat(Amount),
    };
    const netTotal = calculateNetTotal();
    const currentPaidAmount = parseFloat(salesData.Paid_Amount || 0);
    const newPaidAmount =
      paymentEditIndex !== null
        ? currentPaidAmount -
        parseFloat(paymentHistory[paymentEditIndex]?.Amount || 0) +
        parseFloat(Amount)
        : currentPaidAmount + parseFloat(Amount);

    if (newPaidAmount > netTotal) {
      toast.error(
        `Paid Amount (₹${newPaidAmount.toFixed(
          2
        )}) cannot exceed Net Total (₹${netTotal.toFixed(2)}).`
      );
      return;
    }

    if (paymentEditIndex !== null) {
      const updatedPayments = [...paymentHistory];
      updatedPayments[paymentEditIndex] = newPayment;
      setPaymentHistory(updatedPayments);
    } else {
      setPaymentHistory([...paymentHistory, newPayment]);
    }

    setSalesData((prev) => ({
      ...prev,
      Paid_Amount: newPaidAmount,
    }));

    toast.success(
      paymentEditIndex !== null
        ? "Payment updated successfully!"
        : "Payment added successfully!"
    );

    setPaymentForm({
      Date: new Date().toISOString().split("T")[0],
      Pay_Type: "",
      Amount: "",
    });
    setPaymentEditIndex(null);
    // setShowPaymentModal(false);
  };

  // Delete payment
  const deletePayment = (index) => {
    const updatedPayments = paymentHistory.filter((_, i) => i !== index);
    const newPaidAmount = updatedPayments.reduce(
      (sum, payment) => sum + parseFloat(payment.Amount || 0),
      0
    );
    setPaymentHistory(updatedPayments);
    setSalesData((prev) => ({
      ...prev,
      Paid_Amount: newPaidAmount,
    }));
    toast.success("Payment deleted successfully!");
  };

  // Edit payment
  const editPayment = (payment, index) => {
    setPaymentForm(payment);
    setPaymentEditIndex(index);
    setShowPaymentModal(true);
  };

  // Save sales data
  // const saveSales = async () => {
  //   setIsLoading(true);

  //   if (!salesData.Customer_Name) {
  //     toast.error("Please enter a customer name.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   if (!salesData.Purchase_Date) {
  //     toast.error("Please select a purchase date.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   if (salesItems.length === 0) {
  //     toast.error("Please add at least one sales item.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   for (const item of salesItems) {
  //     const stockItem = stockData.find(
  //       (stock) =>
  //         stock.hsn_code === item.hsn_code &&
  //         stock.category === item.category &&
  //         stock.subcategory === item.sub_category
  //     );
  //     if (
  //       !stockItem ||
  //       parseInt(item.sales_quantity) > parseInt(stockItem.available_quantity)
  //     ) {
  //       toast.error(
  //         `Insufficient stock for ${item.category} - ${item.tyre_size}. Available: ${stockItem?.available_quantity || 0}`
  //       );
  //       setIsLoading(false);
  //       return;
  //     }
  //     if (!item.hsn_code) {
  //       toast.error(
  //         `HSN code is missing for ${item.category} - ${item.tyre_size}.`
  //       );
  //       setIsLoading(false);
  //       return;
  //     }
  //     if (
  //       item.tyre_numbers.length !== item.sales_quantity ||
  //       item.tyre_numbers.some((n) => !n.trim())
  //     ) {
  //       toast.error(`Please enter all tyre numbers for ${item.category}`);
  //       setIsLoading(false);
  //       return;
  //     }
  //     const tyreNumbers = item.tyre_numbers;
  //     const uniqueTyreNumbers = new Set(tyreNumbers.filter((n) => n.trim()));
  //     if (uniqueTyreNumbers.size !== tyreNumbers.length) {
  //       toast.error(`Duplicate tyre numbers found for ${item.category}`);
  //       setIsLoading(false);
  //       return;
  //     }
  //     if (!item.Mrp || parseFloat(item.Mrp) <= 0) {
  //       toast.error(`Please enter a valid MRP for ${item.category}`);
  //       setIsLoading(false);
  //       return;
  //     }
  //   }

  //   const netTotal = calculateNetTotal();
  //   const paidAmount = parseFloat(salesData.Paid_Amount || 0);

  //   const dataToSave = {
  //     Sales_ID: salesData.Sales_ID,
  //     Purchase_Date: salesData.Purchase_Date,
  //     Sub_Total: calculateSubtotal().toFixed(2),
  //     Gst_Amount: calculateGstAmount().toFixed(2),
  //     Net_total: netTotal.toFixed(2),
  //     Paid_Amount: paidAmount.toFixed(2),
  //     GSTIN: salesData.GSTIN || "",
  //     name: salesData.Customer_Name || "",
  //     mobile_number: salesData.mobile_number || "",
  //     balance: (netTotal - paidAmount).toFixed(2),
  //     Sales_Items: salesItems.map((item) => ({
  //       Sales_ID: item.Sales_ID,
  //       branch_name: item.branch_name || "Default Branch",
  //       category: item.category,
  //       sub_category: item.sub_category,
  //       type: item.type,
  //       tyre_size: item.tyre_size,
  //       HSN_code: item.hsn_code,
  //       sales_quantity: parseInt(item.sales_quantity),
  //       // sales_rate: parseFloat(item.sales_rate),
  //       sales_rate: calculateSubtotal().toFixed(2),
  //       sales_gst: parseFloat(item.sales_gst),
  //       tyre_number: item.tyre_numbers.join(","),
  //       Mrp: parseFloat(item.Mrp).toFixed(2),
  //       rate_per: parseFloat(item.Mrp).toFixed(2),
  //     })),
  //     Payment_History: paymentHistory.map((payment) => ({
  //       Sales_ID: salesData.Sales_ID,
  //       Date: payment.Date,
  //       Pay_Type: payment.Pay_Type,
  //       Amount: parseFloat(payment.Amount).toFixed(2),
  //     })),
  //   };

  //   try {
  //     if (editId) {
  //       await axios.put(`${ApiUrl}/sales_data.php?id=${editId}`, dataToSave);
  //       await axios.delete(`${ApiUrl}/sales_payment_history.php`, {
  //         data: { Sales_ID: salesData.Sales_ID },
  //       });
  //       if (dataToSave.Payment_History.length > 0) {
  //         await axios.post(
  //           `${ApiUrl}/sales_payment_history.php`,
  //           dataToSave.Payment_History
  //         );
  //       }
  //       toast.success("Sales and payments updated successfully!");
  //     } else {
  //       await axios.post(`${ApiUrl}/sales_data.php`, dataToSave);
  //       if (dataToSave.Payment_History.length > 0) {
  //         await axios.post(
  //           `${ApiUrl}/sales_payment_history.php`,
  //           dataToSave.Payment_History
  //         );
  //       }
  //       toast.success("Sales and payments added successfully!");
  //     }

  //     navigate("/sales");

  //     for (const item of salesItems) {
  //       const stockItem = stockData.find(
  //         (stock) =>
  //           stock.hsn_code === item.hsn_code &&
  //           stock.category === item.category &&
  //           stock.subcategory === item.sub_category
  //       );
  //       if (stockItem) {
  //         const newQty =
  //           parseInt(stockItem.available_quantity) -
  //           parseInt(item.sales_quantity);
  //         const stockPayload = {
  //           hsn_code: item.hsn_code,
  //           type: item.type,
  //           tyre_size: item.tyre_size,
  //           category: item.category,
  //           subcategory: item.sub_category,
  //           purchase_rate: parseFloat(stockItem.purchase_rate) || 0,
  //           avg_purchase_rate:
  //             parseFloat(stockItem.avg_purchase_rate) ||
  //             parseFloat(stockItem.purchase_rate) || 0,
  //           mrp: stockItem.mrp || "0",
  //           available_quantity: newQty,
  //           total_purchase_quantity:
  //             parseInt(stockItem.total_purchase_quantity) || 0,
  //           sold_quantity:
  //             parseInt(stockItem.sold_quantity || 0) +
  //             parseInt(item.sales_quantity),
  //           total_purchases: parseInt(stockItem.total_purchases) || 1,
  //         };

  //         const stockExists = stockData.some(
  //           (stock) =>
  //             stock.hsn_code === item.hsn_code &&
  //             stock.category === item.category &&
  //             stock.subcategory === item.sub_category
  //         );

  //         if (stockExists) {
  //           await axios.put(
  //             `${ApiUrl}/stock_data.php?hsn_code=${item.hsn_code}&category=${encodeURIComponent(
  //               item.category
  //             )}&subcategory=${encodeURIComponent(item.sub_category)}`,
  //             stockPayload
  //           );
  //         } else {
  //           await axios.post(`${ApiUrl}/stock_data.php`, stockPayload);
  //         }

  //         setStockData((prev) =>
  //           prev.map((stock) =>
  //             stock.hsn_code === item.hsn_code &&
  //               stock.category === item.category &&
  //               stock.subcategory === item.sub_category
  //               ? {
  //                 ...stock,
  //                 available_quantity: newQty.toString(),
  //                 sold_quantity: (
  //                   parseInt(stock.sold_quantity || 0) +
  //                   parseInt(item.sales_quantity)
  //                 ).toString(),
  //               }
  //               : stock
  //           )
  //         );
  //         setQuantities((prev) => ({ ...prev, [item.hsn_code]: 0 }));
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error saving sales:", error);
  //     toast.error(
  //       error.response?.data?.message || `Failed to save sales: ${error.message}`
  //     );
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };



  const saveSales = async () => {
    setIsLoading(true);

    if (!salesData.Customer_Name) {
      toast.error("Please enter a customer name.");
      setIsLoading(false);
      return;
    }

    if (!salesData.Purchase_Date) {
      toast.error("Please select a purchase date.");
      setIsLoading(false);
      return;
    }

    if (salesItems.length === 0) {
      toast.error("Please add at least one sales item.");
      setIsLoading(false);
      return;
    }

    for (const item of salesItems) {
      const stockItem = stockData.find(
        (stock) =>
          stock.hsn_code === item.hsn_code &&
          stock.category === item.category &&
          stock.subcategory === item.sub_category
      );
      if (
        !stockItem ||
        parseInt(item.sales_quantity) > parseInt(stockItem.available_quantity)
      ) {
        toast.error(
          `Insufficient stock for ${item.category} - ${item.tyre_size}. Available: ${stockItem?.available_quantity || 0}`
        );
        setIsLoading(false);
        return;
      }
      if (!item.hsn_code) {
        toast.error(
          `HSN code is missing for ${item.category} - ${item.tyre_size}.`
        );
        setIsLoading(false);
        return;
      }
      if (
        item.tyre_numbers.length !== item.sales_quantity ||
        item.tyre_numbers.some((n) => !n.trim())
      ) {
        toast.error(`Please enter all tyre numbers for ${item.category}`);
        setIsLoading(false);
        return;
      }
      const tyreNumbers = item.tyre_numbers;
      const uniqueTyreNumbers = new Set(tyreNumbers.filter((n) => n.trim()));
      if (uniqueTyreNumbers.size !== tyreNumbers.length) {
        toast.error(`Duplicate tyre numbers found for ${item.category}`);
        setIsLoading(false);
        return;
      }
      if (!item.Mrp || parseFloat(item.Mrp) <= 0) {
        toast.error(`Please enter a valid MRP for ${item.category}`);
        setIsLoading(false);
        return;
      }
    }

    const netTotal = calculateNetTotal();
    const paidAmount = parseFloat(salesData.Paid_Amount || 0);

    const dataToSave = {
      Sales_ID: salesData.Sales_ID,
      Purchase_Date: salesData.Purchase_Date,
      Sub_Total: calculateSubtotal().toFixed(2),
      Gst_Amount: calculateGstAmount().toFixed(2),
      Net_total: netTotal.toFixed(2),
      Paid_Amount: paidAmount.toFixed(2),
      GSTIN: salesData.GSTIN || "",
      name: salesData.Customer_Name || "",
      mobile_number: salesData.mobile_number || "",
      balance: (netTotal - paidAmount).toFixed(2),
      Sales_Items: salesItems.map((item) => ({
        Sales_ID: item.Sales_ID,
        branch_name: item.branch_name || "Default Branch",
        category: item.category,
        sub_category: item.sub_category,
        type: item.type,
        tyre_size: item.tyre_size,
        HSN_code: item.hsn_code,
        sales_quantity: parseInt(item.sales_quantity),
        sales_rate: calculateSubtotal().toFixed(2),
        sales_gst: parseFloat(item.sales_gst),
        tyre_number: item.tyre_numbers.join(","),
        Mrp: parseFloat(item.Mrp).toFixed(2),
        rate_per: parseFloat(item.Mrp).toFixed(2),
      })),
      Payment_History: paymentHistory.map((payment) => ({
        Sales_ID: salesData.Sales_ID,
        Date: payment.Date,
        Pay_Type: payment.Pay_Type,
        Amount: parseFloat(payment.Amount).toFixed(2),
      })),
    };

    try {
      if (editId) {
        await axios.put(`${ApiUrl}/sales_data.php?id=${editId}`, dataToSave);
        await axios.delete(`${ApiUrl}/sales_payment_history.php`, {
          data: { Sales_ID: salesData.Sales_ID },
        });
        if (dataToSave.Payment_History.length > 0) {
          await axios.post(
            `${ApiUrl}/sales_payment_history.php`,
            dataToSave.Payment_History
          );
        }
        toast.success("Sales and payments updated successfully!");
      } else {
        await axios.post(`${ApiUrl}/sales_data.php`, dataToSave);
        if (dataToSave.Payment_History.length > 0) {
          await axios.post(
            `${ApiUrl}/sales_payment_history.php`,
            dataToSave.Payment_History
          );
        }
        toast.success("Sales and payments added successfully!");
      }

      // Check for Sales_ID before navigating to invoice
      if (!salesData.Sales_ID) {
        toast.error("Sales ID is missing. Cannot navigate to invoice.");
        setIsLoading(false);
        return;
      }
      navigate(`/invoice_sales/${salesData.Sales_ID}`);

      for (const item of salesItems) {
        const stockItem = stockData.find(
          (stock) =>
            stock.hsn_code === item.hsn_code &&
            stock.category === item.category &&
            stock.subcategory === item.sub_category
        );
        if (stockItem) {
          const newQty =
            parseInt(stockItem.available_quantity) -
            parseInt(item.sales_quantity);
          const stockPayload = {
            hsn_code: item.hsn_code,
            type: item.type,
            tyre_size: item.tyre_size,
            category: item.category,
            subcategory: item.sub_category,
            purchase_rate: parseFloat(stockItem.purchase_rate) || 0,
            avg_purchase_rate:
              parseFloat(stockItem.avg_purchase_rate) ||
              parseFloat(stockItem.purchase_rate) || 0,
            mrp: stockItem.mrp || "0",
            available_quantity: newQty,
            total_purchase_quantity:
              parseInt(stockItem.total_purchase_quantity) || 0,
            sold_quantity:
              parseInt(stockItem.sold_quantity || 0) +
              parseInt(item.sales_quantity),
            total_purchases: parseInt(stockItem.total_purchases) || 1,
          };

          const stockExists = stockData.some(
            (stock) =>
              stock.hsn_code === item.hsn_code &&
              stock.category === item.category &&
              stock.subcategory === item.sub_category
          );

          if (stockExists) {
            await axios.put(
              `${ApiUrl}/stock_data.php?hsn_code=${item.hsn_code}&category=${encodeURIComponent(
                item.category
              )}&subcategory=${encodeURIComponent(item.sub_category)}`,
              stockPayload
            );
          } else {
            await axios.post(`${ApiUrl}/stock_data.php`, stockPayload);
          }

          setStockData((prev) =>
            prev.map((stock) =>
              stock.hsn_code === item.hsn_code &&
                stock.category === item.category &&
                stock.subcategory === item.sub_category
                ? {
                  ...stock,
                  available_quantity: newQty.toString(),
                  sold_quantity: (
                    parseInt(stock.sold_quantity || 0) +
                    parseInt(item.sales_quantity)
                  ).toString(),
                }
                : stock
            )
          );
          setQuantities((prev) => ({ ...prev, [item.hsn_code]: 0 }));
        }
      }
    } catch (error) {
      console.error("Error saving sales:", error);
      toast.error(
        error.response?.data?.message || `Failed to save sales: ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
};

  // Handle print function
  const handlePrint = () => {
    if (!salesData.Sales_ID) {
      toast.error("Sales ID is missing. Cannot navigate to invoice.");
      return;
    }
    navigate(`/invoice_sales/${salesData.Sales_ID}`);
  };

  // Cancel and navigate back
  const handleCancel = () => {
    navigate("/sales");
  };

  // Handle product submission
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (
      !productData.product_name ||
      !productData.category ||
      !productData.subcategory ||
      !productData.branch_name ||
      !productData.purchase_rate ||
      !productData.gst ||
      !productData.available_quantity ||
      !productData.mrp
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (
      parseFloat(productData.purchase_rate) <= 0 ||
      parseFloat(productData.gst) < 0 ||
      parseFloat(productData.available_quantity) < 0 ||
      parseFloat(productData.mrp) <= 0
    ) {
      toast.error(
        "Purchase rate, GST, available quantity, and MRP must be valid numbers."
      );
      return;
    }
    try {
      const formData = {
        hsn_code: productData.hsn_code,
        category: productData.category,
        subcategory: productData.subcategory,
        product_name: productData.product_name,
        branch_name: productData.branch_name || "Default Branch",
        brand_name: productData.brand_name,
        type: productData.type,
        tyre_size: productData.tyre_size,
        status: productData.status,
        purchase_rate: parseFloat(productData.purchase_rate).toFixed(2),
        gst: parseFloat(productData.gst).toFixed(2),
        available_quantity: parseInt(productData.available_quantity),
        mrp: parseFloat(productData.mrp).toFixed(2),
      };
      await axios.post(`${ApiUrl}/stockdata.php`, formData);
      setProductData({
        hsn_code: "",
        category: "",
        subcategory: "",
        product_name: "",
        branch_name: "Default Branch",
        brand_name: "",
        type: "",
        tyre_size: "",
        status: "Active",
        purchase_rate: "",
        gst: "",
        available_quantity: "",
        mrp: "",
      });
      fetchStockData();
      fetchNextProductId();
      setShowProductModal(false);
      toast.success("Product added successfully!");
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error(error.response?.data?.message || "Failed to add product.");
    }
  };

  // Filter stock data
  const uniqueCategories = [...new Set(stockData.map((item) => item.category))];
  const filteredStockData = stockData.filter(
    (item) =>
      searchTerm === "" ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tyre_size.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white font-sans overflow-x-hidden overflow-y-hidden">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />

      <h2 className="m-0 fs-5 fw-semibold text-dark mx-1 mb-3">New Sales</h2>
      <div className="row">
        <div className="col-lg-3">
          <div className="card p-2 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Product List</h5>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowProductModal(true)}
                title="Add new product"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by category or tyre size"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p className="text-danger">{error}</p>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-bordered table-sm"
                  style={{ fontSize: "13px" }}
                >
                  <thead>
                    <tr>
                      <th></th>
                      <th>Products</th>
                      <th>Avl Qty</th>
                      <th>Sale Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueCategories.map((category) => (
                      <React.Fragment key={category}>
                        <tr>
                          <td style={{ width: "30px" }}>
                            <button
                              className="btn btn-sm btn-outline-secondary p-1"
                              onClick={() => toggleCategory(category)}
                            >
                              {expandedCategories[category] ? (
                                <ChevronUp size={10} />
                              ) : (
                                <ChevronDown size={10} />
                              )}
                            </button>
                          </td>
                          <td colSpan="4" className="fw-bold">
                            {category || "Uncategorized"}
                          </td>
                        </tr>
                        {expandedCategories[category] &&
                          filteredStockData
                            .filter(
                              (item) =>
                                item.category === category &&
                                parseInt(item.available_quantity) > 0
                            )
                            .map((item, index) => (
                              <tr key={item.hsn_code}>
                                <td></td>
                                <td style={{ width: "150px" }}>
                                  {item.subcategory} - {item.tyre_size || "N/A"} -{" "}
                                  {item.type || "N/A"}-({item.mrp})
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: "50px" }}
                                    value={
                                      quantityInputs[item.hsn_code] ||
                                      item.available_quantity
                                    }
                                    onChange={(e) =>
                                      handleStockQuantityChange(
                                        item,
                                        e.target.value
                                      )
                                    }
                                    min="0"
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <button
                                      className="btn btn-sm btn-outline-secondary px-2 py-1"
                                      onClick={() =>
                                        handleQuantityChange(item, "decrease")
                                      }
                                      disabled={quantities[item.hsn_code] === 0}
                                    >
                                      -
                                    </button>
                                    <span className="mx-2">
                                      {quantities[item.hsn_code]}
                                    </span>
                                    <button
                                      className="btn btn-sm btn-outline-secondary px-2 py-1"
                                      onClick={() =>
                                        handleQuantityChange(item, "increase")
                                      }
                                      disabled={
                                        quantities[item.hsn_code] >=
                                        parseInt(item.available_quantity) ||
                                        parseInt(item.available_quantity) === 0
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card border h-100">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 text-center">
                  <thead>
                    <tr style={{ backgroundColor: "#d3d3d3" }}>
                      {/* <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        S.No
                      </th> */}
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Products
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Rate/per
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Qty
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Rate
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        GST
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Amount
                      </th>
                      <th className="p-3 text-start text-white fw-semibold small border-bottom bg-danger">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesItems.length > 0 ? (
                      salesItems.map((item, index) => {
                        // const gstRate = parseFloat(item.sales_gst || 0) / 100;
                        // const mrpIncludingGst = parseFloat(item.Mrp || 0);
                        // const basePrice = (mrpIncludingGst / (1 + gstRate)).toFixed(2);
                        // const totalAmount = (item.sales_quantity * mrpIncludingGst).toFixed(2);



                        const gstRate = parseFloat(item.sales_gst || 0) / 100;
                        const mrpIncludingGst = parseFloat(item.Mrp || 0);
                        const baseMrp1 = (mrpIncludingGst * (1 + gstRate)).toFixed(2);
                        const baseMrp2 = (baseMrp1 - mrpIncludingGst).toFixed(2);
                        const baseMrp = (mrpIncludingGst - baseMrp2).toFixed(2);
                        const totalAmount = (item.sales_quantity * mrpIncludingGst).toFixed(2);


                        return (
                          <React.Fragment key={index}>
                            <tr style={{ fontSize: "15px", fontWeight: "400" }}>
                              {/* <td>{index + 1}</td> */}
                              <td>{`${item.category} - ${item.sub_category} - ${item.tyre_size}`}</td>
                              {/* Rate/per (MRP including GST) */}
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: "80px" }}
                                  value={mrpIncludingGst}
                                  onChange={(e) =>
                                    handleSalesItemChange(index, "Mrp", e.target.value)
                                  }
                                  min="0"
                                  step="0.01"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: "50px" }}
                                  value={item.sales_quantity}
                                  onChange={(e) =>
                                    handleSalesItemChange(
                                      index,
                                      "sales_quantity",
                                      e.target.value
                                    )
                                  }
                                  min="0"
                                />
                              </td>
                              {/* Rate (base price excluding GST) */}
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: "80px" }}
                                  value={baseMrp}
                                  readOnly
                                  disabled
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <input
                                    type="tel"
                                    className="form-control form-control-sm me-2"
                                    style={{ width: "50px" }}
                                    value={item.sales_gst}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        (parseFloat(value) >= 0 &&
                                          parseFloat(value) < 100)
                                      ) {
                                        handleSalesItemChange(
                                          index,
                                          "sales_gst",
                                          value
                                        );
                                      } else {
                                        toast.error(
                                          "GST must be between 0 and 99.99."
                                        );
                                      }
                                    }}
                                    min="0"
                                    max="99.99"
                                    step="0.01"
                                  />
                                </div>
                              </td>
                              <td>₹{totalAmount}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => deleteSalesItem(index)}
                                    className="border-0 bg-transparent cursor-pointer text-muted"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                  {/* <button
                                    onClick={() => editSalesItem(item, index)}
                                    className="border-0 bg-transparent cursor-pointer text-muted"
                                  >
                                    <Edit2 size={16} />
                                  </button> */}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan="8">
                                <div className="card p-2 bg-light border rounded mb-2">
                                  {item.tyre_numbers.map((num, tyreIdx) => (
                                    <div
                                      key={tyreIdx}
                                      className="row align-items-center mb-2"
                                    >
                                      <div className="col-md-5">
                                        <strong>Tyre Number {tyreIdx + 1}</strong>
                                      </div>
                                      <div className="col-md-7">
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={num}
                                          onChange={(e) =>
                                            handleTyreNumberChange(
                                              index,
                                              tyreIdx,
                                              e.target.value
                                            )
                                          }
                                          placeholder={`Enter Tyre Number ${tyreIdx + 1}`}
                                          required
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No sales items added
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>





        <div className="col-lg-3">



          <div className="card" style={{ backgroundColor: "#fff0f5" }}>
            <div className="card-body p-2">
              <div className="row g-2">
                <div className="col-lg-12">
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Invoice No</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="Sales_ID"
                        value={salesData.Sales_ID}
                        readOnly
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "white",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Date</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="Purchase_Date"
                        value={salesData.Purchase_Date}
                        onChange={handleSalesDataChange}
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "white",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* end her first card*/}
          <div className="card mt-2" style={{ backgroundColor: "#fff0f5" }}>
            <div className="card-body p-2">
              <div className="row g-2">
                <div className="col-lg-12">
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Customer</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="Customer_Name"
                        value={salesData.Customer_Name}
                        onChange={handleSalesDataChange}
                        placeholder="Enter Customer Name"
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Mobile No</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="mobile_number"
                        value={salesData.mobile_number}
                        onChange={handleSalesDataChange}
                        placeholder="Enter Mobile Number"
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">GSTIN</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="GSTIN"
                        value={salesData.GSTIN}
                        onChange={handleSalesDataChange}
                        placeholder="Enter GSTIN"
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
          {/* second card end here  */}
          <div className="card mt-2" style={{ backgroundColor: "#fff0f5" }}>
            <div className="card-body p-3">
              <div className="row g-2">
                <div className="col-12">


                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Sub Total</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        className="form-control form-control-sm"
                        value={`₹${calculateSubtotal().toFixed(2)}`}
                        readOnly
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "#f9f9f9",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">
                        GST
                        <input
                          type="checkbox"
                          checked={isGstDisabled}
                          onChange={handleGstCheckboxChange}
                          title="Toggle GST calculations"
                        />
                      </label>
                    </div>
                    <div className="col-md-7">
                      <input
                        className="form-control form-control-sm"
                        value={`₹${calculateGstAmount().toFixed(2)}`}
                        readOnly
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "#f9f9f9",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Net Total</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        className="form-control form-control-sm"
                        value={`₹${calculateNetTotal().toFixed(2)}`}
                        readOnly
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "#f9f9f9",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <label className="form-label small fw-medium">Paid Amount</label>
                    </div>
                    <div className="col-md-7">
                      <input
                        className="form-control form-control-sm"
                        name="Paid_Amount"
                        value={`₹${parseFloat(salesData.Paid_Amount || 0).toFixed(2)}`}
                        readOnly
                        style={{
                          padding: "10px",
                          border: "1px solid #d1d3e2",
                          borderRadius: "4px",
                          fontSize: "14px",
                          background: "#f9f9f9",
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-12">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => setShowPaymentModal(true)}
                          disabled={isLoading}
                        >
                          Add Payment
                        </button>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={saveSales}
                          disabled={isLoading}
                        >
                          {isLoading ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handlePrint}
                          disabled={isLoading}
                        >
                          Print
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={handleCancel}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showProductModal ? "show" : ""}`}
        style={{
          display: showProductModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content rounded"
            style={{ backgroundColor: "rgb(255, 240, 245)" }}
          >
            <div className="modal-header bg-pink-light border-bottom-0">
              <h5 className="modal-title m-0 fw-semibold">Add Product</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowProductModal(false)}
                disabled={isLoading}
              ></button>
            </div>
            <div className="modal-body bg-light">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    HSN Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={generatedProductId}
                    readOnly
                    placeholder="Auto-generated HSN Code"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Product Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={productData.product_name}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        product_name: e.target.value,
                      })
                    }
                    placeholder="Enter Product Name"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={productData.category}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        category: e.target.value,
                        subcategory: "",
                      })
                    }
                    required
                  >
                    <option value="">Select Category</option>
                    {categoryTableData.map((category, index) => (
                      <option key={index} value={category.category}>
                        {category.category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Subcategory <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={productData.subcategory}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        subcategory: e.target.value,
                      })
                    }
                    disabled={!productData.category}
                    required
                  >
                    <option value="">Select Subcategory</option>
                    {productData.category &&
                      categoryTableData
                        .find((cat) => cat.category === productData.category)
                        ?.subcategories?.map((subcat, index) => (
                          <option key={index} value={subcat}>
                            {subcat}
                          </option>
                        ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Branch Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={productData.branch_name}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        branch_name: e.target.value,
                      })
                    }
                    placeholder="Enter Branch Name"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Brand Name</label>
                  <select
                    className="form-select form-select-sm"
                    value={productData.brand_name}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        brand_name: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Brand Name</option>
                    <option value="MRF">MRF</option>
                    <option value="CEAT">CEAT</option>
                    <option value="Apollo">Apollo</option>
                    <option value="JK Tyre">JK Tyre</option>
                    <option value="Bridgestone">Bridgestone</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={productData.type}
                    onChange={(e) =>
                      setProductData({ ...productData, type: e.target.value })
                    }
                  >
                    <option value="">Select Type</option>
                    <option value="Radial">Radial</option>
                    <option value="Bias">Bias</option>
                    <option value="Tubeless">Tubeless</option>
                    <option value="Tube Type">Tube Type</option>
                    <option value="Solid">Solid</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Tyre Size</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={productData.tyre_size}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        tyre_size: e.target.value,
                      })
                    }
                    placeholder="Enter Tyre Size"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Available Quantity <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={productData.available_quantity}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        available_quantity: e.target.value,
                      })
                    }
                    placeholder="Enter Available Quantity"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Product Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={productData.status}
                    onChange={(e) =>
                      setProductData({ ...productData, status: e.target.value })
                    }
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    Purchase Rate <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={productData.purchase_rate || ""}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        purchase_rate: e.target.value,
                      })
                    }
                    placeholder="Enter Purchase Rate"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    GST (%) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={productData.gst || ""}
                    onChange={(e) =>
                      setProductData({ ...productData, gst: e.target.value })
                    }
                    placeholder="Enter GST Percentage"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">
                    MRP <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={productData.mrp || ""}
                    onChange={(e) =>
                      setProductData({ ...productData, mrp: e.target.value })
                    }
                    placeholder="Enter MRP"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer border-top-0">
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => setShowProductModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={handleProductSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Adding..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showPaymentModal ? "show" : ""}`}
        style={{
          display: showPaymentModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog">
          <div className="modal-content" style={{ backgroundColor: "#fff0f5" }}>
            <div className="modal-header bg-pink-light border-bottom-0">
              <h5 className="modal-title m-0 fw-semibold">
                {paymentEditIndex !== null ? "Edit Payment" : "Add Payment"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentEditIndex(null);
                  setPaymentForm({
                    Date: new Date().toISOString().split("T")[0],
                    Pay_Type: "",
                    Amount: "",
                  });
                }}
                disabled={isLoading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mx-4">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label fw-medium small">
                      Balance:
                      <input
                        className="form-control form-control-sm mt-1 text-success"
                        value={`₹${(
                          calculateNetTotal() - (Number(salesData.Paid_Amount) || 0)
                        ).toFixed(2)}`}
                        disabled
                      />
                    </label>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">
                      Paid:
                      <input
                        className="form-control form-control-sm mt-1 text-success"
                        value={`₹${Number(salesData.Paid_Amount || 0).toFixed(2)}`}
                        disabled
                      />
                    </label>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label small fw-medium">
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="Date"
                      value={paymentForm.Date}
                      onChange={handlePaymentChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
              <div className="mx-4 mt-3">
                <div className="row g-2">
                  <div className="col-md-5">
                    <select
                      className="form-select form-select-sm"
                      name="Pay_Type"
                      value={paymentForm.Pay_Type}
                      onChange={handlePaymentChange}
                      disabled={isLoading}
                    >
                      <option value="">Select Pay Type</option>
                      <option value="Cash">Cash</option>
                      <option value="Gpay">Gpay</option>
                      <option value="Phonepay">Phonepay</option>
                      <option value="Paytm">Paytm</option>
                      <option value="Card">Card</option>
                      <option value="Credit">Credit</option>
                      <option value="Debit">Debit</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <input
                      className="form-control form-control-sm"
                      name="Amount"
                      type="number"
                      value={paymentForm.Amount}
                      onChange={handlePaymentChange}
                      placeholder="Enter Amount"
                      disabled={isLoading}
                      min="0"
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handlePaymentSubmit}
                      disabled={isLoading}
                    >
                      {paymentEditIndex !== null ? "Update" : "Add +"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="card mx-3 mt-3 mb-3">
                <div className="table-responsive">
                  <table className="table table-bordered text-center">
                    <thead className="small fw-light">
                      <tr>
                        <th>S.No</th>
                        <th>Date</th>
                        <th>Pay Type</th>
                        <th>Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.length > 0 ? (
                        paymentHistory.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.Date}</td>
                            <td>{item.Pay_Type}</td>
                            <td>₹{parseFloat(item.Amount).toFixed(2)}</td>
                            <td>
                              <div className="d-flex gap-2 justify-content-center">
                                <button
                                  className="border-0 bg-transparent cursor-pointer text-muted"
                                  onClick={() => deletePayment(index)}
                                  disabled={isLoading}
                                >
                                  <Trash2 size={16} />
                                </button>
                                <button
                                  className="border-0 bg-transparent cursor-pointer text-muted"
                                  onClick={() => editPayment(item, index)}
                                  disabled={isLoading}
                                >
                                  <Edit2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No payment history available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="d-flex justify-content-end mx-4 mb-3">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentEditIndex(null);
                    setPaymentForm({
                      Date: new Date().toISOString().split("T")[0],
                      Pay_Type: "",
                      Amount: "",
                    });
                  }}
                  disabled={isLoading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigatesales;